import React, { ReactNode } from 'react';
import { Pressable, StyleSheet } from 'react-native';

import { Text } from '@/components/TextComponent';
import { ds } from '@/components/lib/design.system';
import { useHover } from '@/feature/responsive-design/hooks/use-hover';

type DesktopNavChipProps = {
    label?: string;
    icon?: ReactNode;
    trailingIcon?: ReactNode;
    onPress: () => void;
    active?: boolean;
    disabled?: boolean;
    accessibilityLabel?: string;
    testID?: string;
};

/**
 * Primary nav item chip of the desktop web header
 */
export const DesktopNavChip = ({
    label,
    icon,
    trailingIcon,
    onPress,
    active = false,
    disabled = false,
    accessibilityLabel,
    testID,
}: DesktopNavChipProps) => {
    const { hovered, hoverHandlers } = useHover();

    const isIconOnly = !label;
    const showHoverStyle = hovered && !active && !disabled;
    const labelColor = disabled
        ? ds.colorToken.text.disabled
        : active || hovered
        ? ds.colorToken.text.primary
        : ds.colorToken.text.secondary;

    return (
        <Pressable
            {...hoverHandlers}
            onPress={onPress}
            disabled={disabled}
            style={[
                isIconOnly ? styles.iconChip : styles.chip,
                showHoverStyle ? styles.chipHovered : null,
                disabled && isIconOnly ? styles.iconDisabled : null,
            ]}
            accessibilityRole="button"
            accessibilityLabel={accessibilityLabel ?? label}
            accessibilityState={{ disabled, selected: active }}
            testID={testID}
        >
            {icon}
            {label ? (
                <Text style={styles.label} color={labelColor} numberOfLines={1}>
                    {label}
                </Text>
            ) : null}
            {trailingIcon}
        </Pressable>
    );
};

const styles = StyleSheet.create({
    chip: {
        alignItems: 'center',
        borderColor: 'transparent',
        borderRadius: 10,
        borderWidth: 1,
        flexDirection: 'row',
        gap: 2,
        paddingHorizontal: 9,
        paddingVertical: 7,
    },
    iconChip: {
        alignItems: 'center',
        backgroundColor: ds.colorValue.background.neutral.subtler,
        borderColor: ds.colorValue.border.subtle,
        borderRadius: 9999,
        borderWidth: 1,
        height: 36,
        justifyContent: 'center',
        width: 36,
    },
    chipHovered: {
        backgroundColor: ds.colorValue.background.neutral.subtler,
    },
    iconDisabled: {
        opacity: 0.4,
    },
    label: {
        fontSize: 16,
        fontWeight: '500',
        letterSpacing: -0.31,
        lineHeight: 20,
    },
});
